import { useCallback, useMemo, useState } from "react";
import {
  OnestBoldDefault,
  OnestNormalDefault,
  OnestNormalSmall,
} from "../../../components/styled/TextComponents";
import {
  color_blue,
  color_blue_light,
  color_green_light,
  color_grey_dark,
  color_grey_light,
  color_red_ultra_light,
  color_white,
  color_yellow_light,
} from "../../../constants/colors";
import { englishLevelsObject } from "../../../constants/levels";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import {
  EVENT_H_GAP,
  TIME_GUTTER_WIDTH,
  getTimelineHourLabels,
  layoutDayEvents,
} from "../../../utilities/dayTimelineLayout";
import { isLessonPast } from "../../../utilities/lessonHistoryUtils";
import { useI18n } from "../../../i18n/I18nProvider";

const EVENT_TIME_COLUMN_WIDTH = 106;
const EVENT_TIME_SEPARATOR_HEIGHT = 18;

function parseLessonTimeSlot(timeSlot) {
  const parts = timeSlot?.split(" - ") || [];
  if (parts.length >= 2) {
    return { startTime: parts[0], endTime: parts[1] };
  }
  return { startTime: timeSlot || "", endTime: "" };
}

function getLessonColor(paymentStatus) {
  if (paymentStatus === "Не оплачено") {
    return color_red_ultra_light;
  }
  if (paymentStatus === "Последний урок") {
    return color_yellow_light;
  }
  if (paymentStatus === "Оплачено") {
    return color_green_light;
  }
  return color_blue_light;
}

const LessonMetaChip = ({ children }) => (
  <OnestNormalSmall
    style={{
      color: color_grey_dark,
      whiteSpace: "nowrap",
      backgroundColor: color_white,
      borderRadius: 6,
      padding: "2px 6px",
      border: `1px solid ${color_grey_light}`,
      fontSize: 11,
      flexShrink: 0,
    }}
  >
    {children}
  </OnestNormalSmall>
);

const DayEventCard = ({
  layoutItem,
  timeZone,
  onOpenModalReschedule,
  onOpenModalNote,
  onOpenPersonalTaskModal,
  onDeletePersonalTask,
  onDeleteLesson,
}) => {
  const { t } = useI18n();
  const [isHovered, setIsHovered] = useState(false);
  const { event, top, height, column, totalColumns, tier } = layoutItem;
  const isPersonal = event.eventType === "personal";
  const isPastLesson = !isPersonal && isLessonPast(event, timeZone);
  const { startTime, endTime } = parseLessonTimeSlot(event.timeSlot);
  const backgroundColor = isPersonal
    ? color_blue_light
    : getLessonColor(event.paymentStatus);
  const levelLabel = !isPersonal ? englishLevelsObject[event.studentLevel] : null;

  const widthPercent = 100 / totalColumns;
  const leftPercent = column * widthPercent;

  const handleCardClick = useCallback(() => {
    if (isPersonal) {
      onOpenPersonalTaskModal(event);
      return;
    }
    if (isPastLesson) {
      onOpenModalNote(event);
      return;
    }
    onOpenModalReschedule(event);
  }, [
    event,
    isPastLesson,
    isPersonal,
    onOpenModalNote,
    onOpenModalReschedule,
    onOpenPersonalTaskModal,
  ]);

  const handleDeleteClick = useCallback(
    (clickEvent) => {
      clickEvent.stopPropagation();
      if (isPersonal) {
        onDeletePersonalTask(event);
        return;
      }
      onDeleteLesson(event);
    },
    [event, isPersonal, onDeleteLesson, onDeletePersonalTask],
  );

  const timeLabel = endTime ? `${startTime} – ${endTime}` : startTime;
  const title = isPersonal ? event.title : event.studentName;
  const subtitle = event.note || null;
  const showLessonMeta = !isPersonal && tier === "full";

  const fontSize = tier === "micro" ? 11 : 14;
  const paddingRight = isHovered && !isPastLesson ? 28 : tier === "micro" ? 6 : 8;
  const paddingLeft = tier === "micro" ? 6 : 8;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(keyboardEvent) => {
        if (keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
          handleCardClick();
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: "absolute",
        top,
        left: `calc(${leftPercent}% + ${EVENT_H_GAP / 2}px)`,
        width: `calc(${widthPercent}% - ${EVENT_H_GAP}px)`,
        height,
        borderRadius: 8,
        backgroundColor,
        border: isHovered ? `2px solid ${color_blue}` : "2px solid transparent",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        padding: `2px ${paddingRight}px 2px ${paddingLeft}px`,
        boxSizing: "border-box",
        overflow: "hidden",
        cursor: "pointer",
        zIndex: 1 + column,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: tier === "micro" ? 4 : 8,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {tier !== "micro" && (
          <OnestBoldDefault
            style={{
              width: EVENT_TIME_COLUMN_WIDTH,
              minWidth: EVENT_TIME_COLUMN_WIDTH,
              flexShrink: 0,
              whiteSpace: "nowrap",
              lineHeight: 1.2,
              fontSize,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {timeLabel}
          </OnestBoldDefault>
        )}
        {tier !== "micro" && (
          <div
            style={{
              width: 1,
              height: EVENT_TIME_SEPARATOR_HEIGHT,
              backgroundColor: color_grey_light,
              flexShrink: 0,
            }}
          />
        )}
        <OnestNormalDefault
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            fontSize,
            lineHeight: 1.2,
          }}
        >
          {tier === "micro" && (
            <span style={{ flexShrink: 0, fontWeight: 600 }}>{startTime}</span>
          )}
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </span>
          {subtitle && tier !== "micro" && (
            <span
              style={{
                color: color_grey_dark,
                opacity: 0.75,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              · {subtitle}
            </span>
          )}
        </OnestNormalDefault>
        {showLessonMeta && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 4,
              flexShrink: 0,
            }}
          >
            {event.paymentStatus === "Цена не указана" && (
              <LessonMetaChip>{t("Цена не указана")}</LessonMetaChip>
            )}
            {event.lessonQuantityText && (
              <LessonMetaChip>{t(event.lessonQuantityText)}</LessonMetaChip>
            )}
            {levelLabel && <LessonMetaChip>{levelLabel}</LessonMetaChip>}
          </div>
        )}
      </div>
      {isHovered && !isPastLesson && (
        <button
          type="button"
          aria-label={t(isPersonal ? "Удалить событие" : "Удалить урок")}
          title={t("Удалить")}
          onClick={handleDeleteClick}
          style={{
            position: "absolute",
            top: "50%",
            right: 6,
            transform: "translateY(-50%)",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            padding: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <DeleteIcon width={tier === "micro" ? 12 : 16} height={tier === "micro" ? 12 : 16} />
        </button>
      )}
    </div>
  );
};

export const DayTimeline = ({
  events,
  timeZone,
  onOpenModalReschedule,
  onOpenModalNote,
  onOpenPersonalTaskModal,
  onDeletePersonalTask,
  onDeleteLesson,
}) => {
  const layout = useMemo(
    () => layoutDayEvents(events, timeZone),
    [events, timeZone],
  );

  const hourLabels = useMemo(
    () => getTimelineHourLabels(layout.dayStartHour, layout.dayEndHour),
    [layout.dayStartHour, layout.dayEndHour],
  );

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        minWidth: 0,
        height: layout.timelineHeight,
      }}
    >
      <div style={{ display: "flex", gap: 8, width: "100%", height: "100%" }}>
        <div
          style={{
            width: TIME_GUTTER_WIDTH,
            flexShrink: 0,
            position: "relative",
            height: layout.timelineHeight,
          }}
        >
          {hourLabels.map((hour) => {
            const top = (hour - layout.dayStartHour) * 60 * layout.pxPerMinute;
            return (
              <OnestNormalSmall
                key={hour}
                style={{
                  position: "absolute",
                  top: Math.max(0, top - 8),
                  right: 0,
                  color: color_grey_dark,
                  lineHeight: 1,
                  zIndex: 2,
                  backgroundColor: color_white,
                  paddingRight: 4,
                }}
              >
                {String(hour).padStart(2, "0")}:00
              </OnestNormalSmall>
            );
          })}
        </div>
        <div
          style={{
            flex: 1,
            position: "relative",
            height: layout.timelineHeight,
            minWidth: 0,
          }}
        >
          {layout.items.map((layoutItem) => (
            <DayEventCard
              key={layoutItem.event.id}
              layoutItem={layoutItem}
              timeZone={timeZone}
              onOpenModalReschedule={onOpenModalReschedule}
              onOpenModalNote={onOpenModalNote}
              onOpenPersonalTaskModal={onOpenPersonalTaskModal}
              onDeletePersonalTask={onDeletePersonalTask}
              onDeleteLesson={onDeleteLesson}
            />
          ))}
          {hourLabels.map((hour) => {
            const top = (hour - layout.dayStartHour) * 60 * layout.pxPerMinute;
            return (
              <div
                key={`line-${hour}`}
                style={{
                  position: "absolute",
                  top,
                  left: 0,
                  right: 0,
                  height: 1,
                  backgroundColor: color_grey_light,
                  pointerEvents: "none",
                  zIndex: 3,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
